import { useState, useEffect } from 'react';
import { FreightCardGridView } from './FreightCardGridView';
import { ETransportFreightPlutoDto, GetFreightsParams, MinimalTarget } from '@/generated/model';
import { getFreights } from '@/generated/client/etransport/etransport';
import { TargetFreightsDetail } from './TargetFreightsDetails';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { TargetFreightsSubmitForm } from './TargetFreightsSubmitForm';
import { FreightSubmitForm } from './FreightSubmitForm';
import { dbToArray } from '@/components/db/query';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import './etransport.css';

export default function EtransportPopup({ close }: { close: () => void }) {
  // const { t } = useTranslation();
  // const userCtx = useContext(ctxUser);

  const [freights, setFreights] = useState<ETransportFreightPlutoDto[] | undefined>(undefined);
  const [targets, setTargets] = useState<MinimalTarget[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newChangesAdded, setNewChangesAdded] = useState<FreightDataChange | null>(null);
  const [openTargetFreightSubmitForm, setOpenTargetFreightSubmitForm] = useState<boolean>(false);
  const [openTargetFreightDetails, setOpenTargetFreightDetails] = useState<boolean>(false);
  const [openFreightSubmitForm, setOpenFreightSubmitForm] = useState<boolean>(false);
  const [currentFreight, setCurrentFreight] = useState<ETransportFreightPlutoDto | undefined>(undefined);
  const [searchTarget, setSearchTarget] = useState<string | undefined>(undefined);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);
  const [isClosingDetails, setIsClosingDetails] = useState<boolean>(false);
  const [isClosingSubmitForm, setIsClosingSubmitForm] = useState<boolean>(false);
  const isPhoneRes: boolean = isPhoneOrHighScreen();

  useEffect(() => {
    async function fetchInitialData() {
      setIsLoading(true);
      try {
        const [freightsResult, targetsResult] = await Promise.all([getFreights(), dbToArray(0, 1000, 'asc')]);
        const freights: ETransportFreightPlutoDto[] = freightsResult.data;
        setFreights(freights);
        setTargets(targetsResult);
      } catch (err) {
        setFreights(undefined);
        setTargets(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!refetchTrigger) return;

    async function fetchInitialData() {
      setIsLoading(true);
      try {
        const [freightsResult, targetsResult] = await Promise.all([getFreights(), dbToArray(0, 1000, 'asc')]);
        const freights: ETransportFreightPlutoDto[] = freightsResult.data;
        setFreights(freights);
        setTargets(targetsResult);
        updateCurrentFreight(freights);
      } catch (err) {
        setFreights(undefined);
        setTargets(undefined);
        setCurrentFreight(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    function updateCurrentFreight(freights: ETransportFreightPlutoDto[]) {
      const freight: ETransportFreightPlutoDto = freights.filter((freight) => freight.uit === currentFreight?.uit)[0];
      setCurrentFreight(freight);
    }

    fetchInitialData();
  }, [refetchTrigger]);

  useEffect(() => {
    if (searchTarget === undefined) return;

    async function fetchFilteredFreights() {
      setIsLoading(true);
      const emptySearch: boolean = searchTarget === '';
      let filteredFreights: ETransportFreightPlutoDto[] = [];

      try {
        const result = await getFreights();
        filteredFreights = result.data;
      } catch (err) {
        console.error('Error fetching data:', err);
      }

      //Filter for UIT
      if (searchTarget !== undefined && !emptySearch) {
        filteredFreights = filteredFreights.filter((freight) => freight.uit?.includes(searchTarget)) || [];
      }

      //Filter for target
      try {
        if (!emptySearch) {
          const result = await getFreights({ filter: searchTarget } satisfies GetFreightsParams);
          filteredFreights = [...result.data, ...filteredFreights];
        }
        setFreights(filteredFreights);
      } catch (err) {
        setFreights(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFilteredFreights();
  }, [searchTarget]);

  function handleRefetch(): void {
    setRefetchTrigger((prev) => prev + 1);
  }

  return isPhoneRes ? (
    <Dialog open={true} onClose={close}>
      <DialogTitle>e-Transport</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0% 10%',
          }}
        >
          <img
            src="svg/NoSearchResult_ez_transparent.svg"
            alt="not_found"
            style={{ width: '100%', maxHeight: '250px' }}
            data-qa-element-id="no_result_image"
          />

          <Typography
            textAlign={'center'}
            sx={{ fontSize: '14px', fontWeight: 500 }}
            gutterBottom
            data-qa-element-id="no_result_text"
          >
            Under construction...
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  ) : (
    <div style={{ position: 'relative', width: '500px' }}>
      <div
        className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base"
        data-qa-element-id="etr_freight_card_grid"
      >
        <FreightCardGridView
          targets={targets}
          freights={freights}
          isLoading={isLoading}
          searchTarget={searchTarget}
          currentFreight={currentFreight}
          onRefetch={handleRefetch}
          setNewChanges={setNewChangesAdded}
          setSearchTarget={setSearchTarget}
          setCurrentFreight={setCurrentFreight}
          setOpenFreightSubmitForm={setOpenFreightSubmitForm}
          setOpenTargetFreightDetails={setOpenTargetFreightDetails}
          setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
        />
      </div>
      {(openTargetFreightDetails || isClosingDetails) && (
        <div
          className={`etransport-overlay-container ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_detail_panel"
          >
            <TargetFreightsDetail
              targets={targets}
              freight={currentFreight}
              newChange={newChangesAdded}
              openTargetFreightSubmitForm={openTargetFreightSubmitForm}
              onRefetch={handleRefetch}
              setNewChanges={setNewChangesAdded}
              setCurrentFreight={setCurrentFreight}
              setIsClosingDetails={setIsClosingDetails}
              setOpenTargetFreightDetails={setOpenTargetFreightDetails}
              setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
            />
          </div>
          {openTargetFreightSubmitForm && (
            <div className="etransport-freight-panel" data-qa-element-id="etr_freight_target_submit_panel">
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            </div>
          )}
        </div>
      )}
      {(openFreightSubmitForm || isClosingSubmitForm) && (
        <div
          className={`etransport-overlay-container ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
